// ResetPasswordWizard.jsx - Modal de recuperación de contraseña (3 pasos)
// Ticket: J410772 - Frontend: flujo completo "Olvidé mi contraseña"
// Patrón: Modal wizard integrado en /login (sin rutas nuevas)

import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { Eye, EyeOff, AlertCircle, Check, Mail, Key, Lock, ArrowRight, ArrowLeft, RefreshCw, Edit3, Circle, CheckCircle, XCircle } from 'lucide-react';

const ResetPasswordWizard = ({ onClose, onSuccess }) => {
  // ============================================================================
  // ESTADO DEL WIZARD
  // ============================================================================
  const [step, setStep] = useState(1); // 1: Email, 2: Código, 3: Nueva contraseña
  
  // STEP 1: Email
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(null);
  
  // STEP 2: Código de verificación
  const [code, setCode] = useState('');
  const [resetSessionId, setResetSessionId] = useState(''); // CRÍTICO: SOLO EN MEMORIA (no localStorage)
  const [resendCooldown, setResendCooldown] = useState(0); // J410772-UX1: Cooldown para reenviar código
  
  // STEP 3: Nueva contraseña
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Estados globales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Validación de contraseña
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false
  });

  // ============================================================================
  // VALIDACIONES
  // ============================================================================
  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  useEffect(() => {
    if (email) {
      setEmailValid(validateEmail(email));
    } else {
      setEmailValid(null);
    }
  }, [email]);

  useEffect(() => {
    const criteria = {
      length: newPassword.length >= 9,
      upper: /[A-Z]/.test(newPassword),
      lower: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(newPassword)
    };
    setPasswordCriteria(criteria);
  }, [newPassword]);

  // J410772-UX1: Cooldown timer para reenvío de código
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const allPasswordCriteriaPassed = Object.values(passwordCriteria).every(v => v);
  const showPasswordRequirements = newPassword.length > 0 && !allPasswordCriteriaPassed;
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  // ============================================================================
  // J410772-UX1: REENVIAR CÓDIGO (reutiliza endpoint del paso 1)
  // ============================================================================
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');

    try {
      await apiClient.post('/forgot-password', { email });
      console.log('[RESEND] Código reenviado (si el email existe)');
      
      setResendCooldown(60); // 60 segundos de cooldown
      setError(''); // Limpiar error previo
    } catch (err) {
      console.error('[RESEND] Error al reenviar:', err);
      // Anti-enumeración: mensaje genérico
      setError('Hubo un problema. Verifica tu conexión e inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // STEP 1: ENVIAR EMAIL (POST /api/forgot-password)
  // ============================================================================
  const handleSendCode = async () => {
    if (!emailValid) {
      setError('Por favor, ingresa un email válido.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/forgot-password', { email });
      
      // Backend responde: { msg: "Si el correo está registrado, recibirás un código..." }
      console.log('✅ [STEP 1] Código solicitado:', response.data);
      
      setStep(2); // Avanzar a ingreso de código
    } catch (err) {
      console.error('❌ [STEP 1] Error al solicitar código:', err);
      
      // Manejo de errores específicos
      const errorCode = err.response?.data?.error;
      const errorMessage = err.response?.data?.message;
      
      if (err.response?.status === 429) {
        setError('Demasiados intentos. Espera unos minutos e intenta de nuevo.');
      } else {
        setError(errorMessage || 'Error al enviar código. Intenta más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // STEP 2: VERIFICAR CÓDIGO (POST /api/verify-reset-code)
  // ============================================================================
  const handleVerifyCode = async () => {
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('El código debe tener exactamente 6 dígitos.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/verify-reset-code', { 
        email, 
        code 
      });
      
      // Backend responde: { resetSessionId: "64-char-hex-string" }
      const { resetSessionId: sessionId } = response.data;
      
      if (!sessionId || sessionId.length !== 64) {
        throw new Error('resetSessionId inválido recibido del servidor');
      }
      
      console.log('✅ [STEP 2] Código verificado, resetSessionId recibido (longitud: %d)', sessionId.length);
      
      // ⚠️ CRÍTICO: Almacenar SOLO EN MEMORIA (no localStorage)
      setResetSessionId(sessionId);
      
      setStep(3); // Avanzar a cambio de contraseña
    } catch (err) {
      console.error('❌ [STEP 2] Error al verificar código:', err);
      
      const errorCode = err.response?.data?.error;
      const errorMessage = err.response?.data?.message;
      
      if (errorCode === 'INVALID_OR_EXPIRED_CODE') {
        setError('Código incorrecto o expirado. Solicita uno nuevo.');
      } else if (err.response?.status === 429) {
        setError('Demasiados intentos. Espera unos minutos.');
      } else {
        setError(errorMessage || 'Error al verificar código. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // STEP 3: CAMBIAR CONTRASEÑA (POST /api/reset-password)
  // ============================================================================
  const handleResetPassword = async () => {
    if (!allPasswordCriteriaPassed) {
      setError('La contraseña no cumple con todos los criterios.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (!resetSessionId) {
      setError('Sesión inválida. Reinicia el proceso.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/reset-password', { 
        email, 
        resetSessionId, 
        newPassword 
      });
      
      console.log('✅ [STEP 3] Contraseña actualizada:', response.data);
      
      setSuccess(true);
      
      // Cerrar modal y mostrar mensaje de éxito después de 2 segundos
      setTimeout(() => {
        onSuccess('Contraseña actualizada correctamente. Ya puedes iniciar sesión.');
        onClose();
      }, 2000);
    } catch (err) {
      console.error('❌ [STEP 3] Error al cambiar contraseña:', err);
      
      const errorCode = err.response?.data?.error;
      const errorMessage = err.response?.data?.message;
      
      if (errorCode === 'INVALID_SESSION') {
        setError('Sesión expirada. Reinicia el proceso.');
      } else if (errorCode === 'WEAK_PASSWORD') {
        setError('La contraseña no cumple con los requisitos de seguridad.');
      } else if (err.response?.status === 429) {
        setError('Demasiados intentos. Espera unos minutos.');
      } else {
        setError(errorMessage || 'Error al actualizar contraseña. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // NAVEGACIÓN DEL WIZARD
  // ============================================================================
  const handleBack = () => {
    setError('');
    if (step === 2) {
      setCode('');
      setStep(1);
    } else if (step === 3) {
      setNewPassword('');
      setConfirmPassword('');
      // ⚠️ NO volver a step 2 desde step 3 (código ya consumido)
      // En su lugar, forzar reinicio completo
      setError('El código ya fue usado. Debes solicitar uno nuevo.');
      setTimeout(() => {
        setStep(1);
        setCode('');
        setResetSessionId('');
      }, 2000);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' && !loading) {
      action();
    }
  };

  // ============================================================================
  // RENDER DEL MODAL
  // ============================================================================
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl">
        {/* Header con stepper */}
        <div className="mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Recuperar contraseña
          </h2>
          
          {/* Stepper visual */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 1 ? 'bg-[#0c203b] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 1 ? <Check size={16} /> : '1'}
              </div>
              <div className={`h-1 flex-1 mx-2 ${step >= 2 ? 'bg-[#0c203b]' : 'bg-gray-200'}`}></div>
            </div>
            
            <div className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= 2 ? 'bg-[#0c203b] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 2 ? <Check size={16} /> : '2'}
              </div>
              <div className={`h-1 flex-1 mx-2 ${step >= 3 ? 'bg-[#0c203b]' : 'bg-gray-200'}`}></div>
            </div>
            
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              step >= 3 ? 'bg-[#0c203b] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              3
            </div>
          </div>
        </div>

        {/* Mensajes de error/éxito */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-4 flex items-start gap-2">
            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="text-sm">¡Contraseña actualizada correctamente!</span>
          </div>
        )}

        {/* STEP 1: Email */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Ingresa tu correo electrónico y te enviaremos un código de verificación.
            </p>
            
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onKeyPress={(e) => handleKeyPress(e, handleSendCode)}
                className={`w-full pl-10 pr-10 py-3 border-2 rounded-xl text-gray-900 focus:ring-2 transition-all ${
                  emailValid === null ? 'border-gray-200 focus:ring-[#0c203b] focus:border-[#0c203b]' :
                  emailValid ? 'border-green-300 focus:ring-green-500 focus:border-green-500' :
                  'border-red-300 focus:ring-red-500 focus:border-red-500'
                }`}
                disabled={loading}
                autoFocus
              />
              {emailValid !== null && (
                <div className="absolute right-3 top-3.5">
                  {emailValid ? (
                    <CheckCircle size={20} className="text-green-500" />
                  ) : (
                    <XCircle size={20} className="text-red-500" />
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={loading || !emailValid}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  loading || !emailValid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#0c203b] text-white hover:bg-[#1a365d] shadow-md hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <span>Enviar código</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Código de verificación - J410772-UX1 */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Copy principal - NO afirma que se envió código */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-900 mb-1">
                Ingresa el código de 6 dígitos
              </p>
              <p className="text-xs text-gray-500">
                Si el correo ingresado está registrado, recibirás un código para continuar.
              </p>
            </div>

            {/* Email ingresado - EDITABLE (no confirmado) */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Mail className="text-gray-400 flex-shrink-0" size={16} />
                <span className="text-sm text-gray-700 truncate">{email}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setCode('');
                  setError('');
                }}
                className="text-xs text-[#0c203b] hover:text-[#1a365d] font-medium flex items-center gap-1 flex-shrink-0"
                disabled={loading}
              >
                <Edit3 size={12} />
                <span>Cambiar</span>
              </button>
            </div>
            
            {/* Input de código */}
            <div className="relative">
              <Key className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Solo dígitos
                  setCode(value);
                  setError('');
                }}
                onKeyPress={(e) => handleKeyPress(e, handleVerifyCode)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-[#0c203b] focus:border-[#0c203b] text-center text-2xl tracking-widest font-mono"
                disabled={loading}
                autoFocus
              />
            </div>

            {/* Texto de ayuda preventivo - J410772-UX1 */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-xs text-blue-700">
              <p className="font-medium mb-1">¿No recibiste el código?</p>
              <p>Revisa tu carpeta de spam o verifica que el correo ingresado sea correcto.</p>
            </div>

            {/* Botón Reenviar código con cooldown - J410772-UX1 */}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading || resendCooldown > 0}
              className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                loading || resendCooldown > 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-[#0c203b] hover:bg-gray-200'
              }`}
            >
              <Mail size={16} />
              {resendCooldown > 0 ? (
                <span>Reenviar código (disponible en {resendCooldown}s)</span>
              ) : (
                <span>Reenviar código</span>
              )}
            </button>

            <div className="flex justify-between gap-2 mt-6">
              <button
                type="button"
                onClick={handleBack}
                className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all font-medium flex items-center gap-2"
                disabled={loading}
              >
                <ArrowLeft size={16} />
                <span>Atrás</span>
              </button>
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={loading || code.length !== 6}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  loading || code.length !== 6
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#0c203b] text-white hover:bg-[#1a365d] shadow-md hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Verificando...</span>
                  </>
                ) : (
                  <>
                    <span>Verificar código</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Nueva contraseña */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Crea una nueva contraseña segura para tu cuenta.
            </p>
            
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Nueva contraseña"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                }}
                className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-[#0c203b] focus:border-[#0c203b]"
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-3.5 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Validaciones de contraseña */}
            {showPasswordRequirements && (
              <div className="bg-gray-50 rounded-xl p-4 text-xs space-y-2 border border-gray-200">
                <p className="font-semibold text-gray-800 mb-2">Requisitos de contraseña:</p>
                <div className="grid grid-cols-1 gap-1.5">
                  <p className={`flex items-center gap-2 ${passwordCriteria.length ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                    {passwordCriteria.length ? <CheckCircle size={16} /> : <Circle size={16} />}
                    <span>Mínimo 9 caracteres</span>
                  </p>
                  <p className={`flex items-center gap-2 ${passwordCriteria.upper ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                    {passwordCriteria.upper ? <CheckCircle size={16} /> : <Circle size={16} />}
                    <span>Una mayúscula</span>
                  </p>
                  <p className={`flex items-center gap-2 ${passwordCriteria.lower ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                    {passwordCriteria.lower ? <CheckCircle size={16} /> : <Circle size={16} />}
                    <span>Una minúscula</span>
                  </p>
                  <p className={`flex items-center gap-2 ${passwordCriteria.number ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                    {passwordCriteria.number ? <CheckCircle size={16} /> : <Circle size={16} />}
                    <span>Un número</span>
                  </p>
                  <p className={`flex items-center gap-2 ${passwordCriteria.symbol ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                    {passwordCriteria.symbol ? <CheckCircle size={16} /> : <Circle size={16} />}
                    <span>Un símbolo (!@#$%^&*)</span>
                  </p>
                </div>
              </div>
            )}

            {/* Confirmar contraseña (solo si password es válido) */}
            {allPasswordCriteriaPassed && (
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  onKeyPress={(e) => handleKeyPress(e, handleResetPassword)}
                  className={`w-full pl-10 pr-20 py-3 border-2 rounded-xl text-gray-900 focus:ring-2 transition-all ${
                    confirmPassword.length === 0 ? 'border-gray-200 focus:ring-[#0c203b] focus:border-[#0c203b]' :
                    passwordsMatch ? 'border-green-300 focus:ring-green-500 focus:border-green-500' :
                    'border-red-300 focus:ring-red-500 focus:border-red-500'
                  }`}
                  disabled={loading}
                />
                <div className="absolute right-3 top-3.5 flex items-center gap-2">
                  {confirmPassword.length > 0 && (
                    passwordsMatch ? (
                      <CheckCircle size={20} className="text-green-500" />
                    ) : (
                      <XCircle size={20} className="text-red-500" />
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Mensaje de contraseñas no coincidentes */}
            {confirmPassword.length > 0 && !passwordsMatch && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <span className="text-red-700 text-sm font-medium">Las contraseñas no coinciden</span>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={loading || !allPasswordCriteriaPassed || !passwordsMatch}
                className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  loading || !allPasswordCriteriaPassed || !passwordsMatch
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#0c203b] text-white hover:bg-[#1a365d] shadow-md hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Actualizando...</span>
                  </>
                ) : (
                  <>
                    <span>Actualizar contraseña</span>
                    <Check size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordWizard;
